import {getDeviceType} from "@/plugins/Helpers";
import Cookies from "@/import/cookies";
import {AnalyticsSourceEnum} from "@/types/AnalyticsSourceEnum";
import { v4 as uuidv4 } from 'uuid';

export default class Analytics {
    private static _data: Array<any> = [];
    private _analyticsData: {[key: string]: string|number} = {};

    static readonly MARKETING_ALLOWED_PARAMS = [
        'ad_source',
        'ad_medium',
        'ad_group',
        'ad_campaign',
        'utm_source',
        'utm_medium',
        'utm_group',
        'utm_campaign',
    ];

    constructor() {
        const marketingParams = this.getMarketingParams();
        if (Object.keys(marketingParams).length > 0) {
            Cookies.set('marketing_params', JSON.stringify(marketingParams), 24 * 365, '/');
        }

        this.storeScreenResolution();
        this.registerEvent();
        this.handleVisits();
    }

    /**
     * Stores current screen resolution into a cookie so the backend
     * can include it in server-side analytics events (e.g. order.created).
     *
     * @private
     */
    private storeScreenResolution(): void {
        if (typeof window === 'undefined' || !window.screen) {
            return;
        }

        const resolution = `${window.screen.width}x${window.screen.height}`;
        Cookies.set('screen_resolution', resolution, 24);
    }

    /**
     * Add new analytics record
     *
     * @param event
     * @param data
     */
    public add<Type>(event: string, data: Type): void {
        if (typeof Analytics._data === 'undefined' || Analytics._data === null) {
            Analytics._data = [];
        }
        Analytics._data.push({
            aid: Cookies.get('aid'),
            event: event,
            source: AnalyticsSourceEnum.SHOP,
            ...data,
            marketing: {
                ...this.getMarketingParams(),
            },
            metadata: {
                url: window.location.href,
                agent: navigator.userAgent,
                // @ts-ignore
                language: window.navigator.userLanguage || window.navigator.language,
                device: getDeviceType(),
                visits: this.visitedPages,
            },
            consent: this.getConsent(),
        });
    }

    private getConsent(): {ad_user_data: string, ad_personalization: string} {
        const marketing = Cookies.get('DisableAllTracking');
        let state = 'unknown';

        if (marketing == '0') {
            state = 'granted';
        } else if (marketing == '1') {
            state = 'denied';
        }

        return {
            ad_user_data: state,
            ad_personalization: state,
        };
    }

    public get data(): Array<any> {
        return Analytics._data;
    }

    /**
     * Return true when evenName is already registered
     * @param eventName
     */
    public hasEvent(eventName: string): boolean {
        return Analytics._data.findIndex(item => item.event === eventName) > -1;
    }

    /**
     * Parse URL query string to object
     *
     * @private
     */
    private getMarketingParams() {
        if (Object.keys(this._analyticsData).length === 0) {
            let data: {[key: string]: string|number} = {};
            const url = window.location.href;
            const paramArr = url.slice(url.indexOf('?') + 1).split('&');
            paramArr.map(param => {
                const [key, val] = param.split('=');
                if (typeof val !== 'undefined') {
                    data[key] = decodeURIComponent(val);
                }
            });

            for (let paramName of Analytics.MARKETING_ALLOWED_PARAMS) {
                if (data.hasOwnProperty(paramName)) {
                    this._analyticsData[paramName] = data[paramName];
                }
            }
        }

        return this._analyticsData;
    }

    private registerEvent(): void {
        var analytics = this;
        window.addEventListener('pagehide', function(event) {
            if (typeof navigator.sendBeacon === 'function' && analytics.data.length > 0) {
                analytics.send();
            }
        });
    }

    private get visitedPages(): number {
        let av: string|number|null = Cookies.get('all_visits');
        return av !== null ? parseInt(av) : 0;
    }

    private handleVisits(): void {
        let currentTimestamp = (new Date()).getTime();
        let timeToExpire = 16; // 16 hours
        let av: string|number|null = Cookies.get('all_visits');
        let avu: string|number|null = Cookies.get("all_visits_updated");

        if (av == null) {
            av = 0;
            Cookies.set('all_visits', 0);
            Cookies.set('all_visits_updated', currentTimestamp);
        }

        av = parseInt(av as string);
        if (Cookies.get("current_visit") == null) {
            Cookies.set('current_visit', true, 1.50); //half an hour
            av++;
            Cookies.set('all_visits', av);
            Cookies.set('all_visits_updated', currentTimestamp);
        } else if (
            avu !== null
            && parseInt(avu) < (currentTimestamp - timeToExpire)
        ) {
            av++;
            Cookies.set('all_visits', av);
            Cookies.set('all_visits_updated', currentTimestamp);
        }

        if (Cookies.get('aid') == null) {
            Cookies.set('aid', uuidv4(), 365 * 24);
        }
    }

    /**
     * Remove all events
     *
     * @private
     */
    public clear(): void {
        Analytics._data.splice(0);
    }

    /**
     * Send all added events
     *
     * @private
     */
    public send(): void {
        const blob = new Blob([JSON.stringify(this.data)], {type : 'application/json'});
        navigator.sendBeacon('/api/analytics', blob);
        this.clear();
    }
}

window.beacon = new Analytics();
